import { Box, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import { IconEnum } from '@pxtra-client-lib-legacy/gen';
import { FC } from 'react';
import { t } from 'i18next';
import {
  SettingsIcon,
  BusIcon,
  CarChargingIcon,
  CarChargingCableIcon,
  CarFrontIcon,
  CarSideIcon,
  ChargingStationElectricityIcon,
  ChefHatIcon,
  CoffeeIcon,
  CreditcardIcon,
  FoodBasketIcon,
  HeadphonesSupportIcon,
  HealthGraphHeartIcon,
  HealthyDietFruitIcon,
  HeartbeatHeartIcon,
  HeartbeatIcon,
  LifebuoyIcon,
  LotusIcon,
  PhoneIcon,
  PiggyBankIcon,
  RoomServiceIcon,
  SaleDiscountIcon,
  SaleDiscountPromotionIcon,
  ScooterIcon,
  ShieldCheckmarkIcon,
  ShieldSquareIcon,
  SingleUserDiscountIcon,
  SmileyIcon,
  SneakersIcon,
  StoreDiscountIcon,
  TruckIcon,
  UserHeartHealthIcon,
  VipIcon,
  YinyangIcon,
  AppleIcon
} from '../../../icons';
import { IconProps } from '../../../icons/templates/icon';
import Util from '../../../../misc/util';

interface Props {
  iconKey: IconEnum;
  sx?: SxProps<Theme>;
  size?: number;
  backgroundColor?: string;
  color?: string;
}

type Group =
  | 'mobility'
  | 'food'
  | 'diverse'
  | 'payment_in_kind'
  | 'everyday_life'
  | 'sports'
  | 'discounts'
  | 'insurances'
  | 'recreation';

type Benefit = {
  icon: FC<IconProps>;
  group: Group;
};

const benefitMap: Record<IconEnum, Benefit> = {
  settings: { icon: SettingsIcon, group: 'diverse' },
  apple: { icon: AppleIcon, group: 'food' },
  bus: { icon: BusIcon, group: 'mobility' },
  car_front: { icon: CarFrontIcon, group: 'mobility' },
  car_side: { icon: CarSideIcon, group: 'mobility' },
  car_charging: { icon: CarChargingIcon, group: 'mobility' },
  car_charging_cable: { icon: CarChargingCableIcon, group: 'mobility' },
  car_charging_station: { icon: ChargingStationElectricityIcon, group: 'mobility' },
  chef_hat: { icon: ChefHatIcon, group: 'food' },
  coffee: { icon: CoffeeIcon, group: 'food' },
  creditcard: { icon: CreditcardIcon, group: 'payment_in_kind' },
  food_basket: { icon: FoodBasketIcon, group: 'food' },
  headphones: { icon: HeadphonesSupportIcon, group: 'everyday_life' },
  health_graph_heart: { icon: HealthGraphHeartIcon, group: 'sports' },
  healthy_diet: { icon: HealthyDietFruitIcon, group: 'food' },
  heartbeat_heart: { icon: HeartbeatHeartIcon, group: 'sports' },
  heartbeat: { icon: HeartbeatIcon, group: 'sports' },
  lifebuoy: { icon: LifebuoyIcon, group: 'insurances' },
  lotus: { icon: LotusIcon, group: 'recreation' },
  phone: { icon: PhoneIcon, group: 'everyday_life' },
  piggy_bank: { icon: PiggyBankIcon, group: 'insurances' },
  room_service: { icon: RoomServiceIcon, group: 'everyday_life' },
  sale_discount: { icon: SaleDiscountIcon, group: 'discounts' },
  sale_discount_promotion: { icon: SaleDiscountPromotionIcon, group: 'discounts' },
  scooter: { icon: ScooterIcon, group: 'mobility' },
  shield_checkmark: { icon: ShieldCheckmarkIcon, group: 'insurances' },
  shield_square: { icon: ShieldSquareIcon, group: 'insurances' },
  discount: { icon: SingleUserDiscountIcon, group: 'discounts' },
  smiley: { icon: SmileyIcon, group: 'diverse' },
  sneaker: { icon: SneakersIcon, group: 'sports' },
  store_discount: { icon: StoreDiscountIcon, group: 'discounts' },
  truck: { icon: TruckIcon, group: 'mobility' },
  user_heart: { icon: UserHeartHealthIcon, group: 'diverse' },
  vip: { icon: VipIcon, group: 'discounts' },
  yinyang: { icon: YinyangIcon, group: 'recreation' }
};

const getBenefitIcon = (benefitKey: IconEnum, color?: string, size?: number) => {
  const Component =
    benefitKey in benefitMap ? benefitMap[benefitKey].icon : benefitMap['settings'].icon;
  return <Component color={color} height={size} width={size} />;
};

export const getIconOptions = () => {
  return Util.getKeys(benefitMap).map((key) => ({
    value: key,
    text: 'x',
    icon: getBenefitIcon(key),
    group: getIconGroup(key)
  }));
};

const getIconGroup = (benefitKey: IconEnum) => {
  const group = benefitMap[benefitKey].group;
  return t(`custom:group_${group}`);
};

export const CustomBenefitIcon: FC<Props> = ({ iconKey, size, backgroundColor, color, sx }) => {
  size = size ?? 40;
  color = color ?? 'white';
  backgroundColor = backgroundColor ?? 'heaven.main';

  return (
    <Box
      sx={{
        backgroundColor: backgroundColor,
        borderRadius: `${size * 0.35}px`,
        ...sx,
        height: size,
        width: size
      }}
    >
      {getBenefitIcon(iconKey, color, size)}
    </Box>
  );
};
